import { barOfBeat, beatInBar, gridEnd } from '@/core/analysis/grid';
import type { AnalysisResult } from '@/core/analysis/types';
import { chordName, LOW_CONFIDENCE, type DisplayOptions } from '@/core/theory/display';

export interface GridCell {
  beat: number;
  /** Chord label on the beat where the chord changes; null on continuation beats. */
  label: string | null;
  kind: 'chord' | 'no-chord' | 'continue';
  low: boolean;
  aria: string;
}

export interface GridBar {
  /** 1-based; 0 is the pickup before the first downbeat. */
  number: number;
  /** Always beatsPerBar long; null pads a pickup bar so its beats line up at the end. */
  cells: (GridCell | null)[];
  start: number;
  end: number;
}

export function buildGrid(result: AnalysisResult, o: DisplayOptions): GridBar[] {
  const grid = result.beatGrid;
  const { beats, beatsPerBar } = grid;
  const segmentOfBeat = new Array<number>(beats.length);
  result.segments.forEach((seg, si) => {
    for (let b = seg.startBeat; b < seg.startBeat + seg.beatCount; b++) segmentOfBeat[b] = si;
  });

  const bars: GridBar[] = [];
  beats.forEach((time, beat) => {
    const number = barOfBeat(grid, beat);
    const position = beatInBar(grid, beat);
    let bar = bars.at(-1);
    if (bar?.number !== number) {
      bar = {
        number,
        cells: new Array<GridCell | null>(beatsPerBar).fill(null),
        start: time,
        end: time,
      };
      bars.push(bar);
    }
    const seg = result.segments[segmentOfBeat[beat] ?? -1];
    const isStart = seg?.startBeat === beat;
    const label = seg && isStart ? chordName(seg.chord, o) : null;
    const low = Boolean(isStart && seg.chord && seg.confidence < LOW_CONFIDENCE);
    bar.cells[position - 1] = {
      beat,
      label,
      kind: !isStart ? 'continue' : seg.chord ? 'chord' : 'no-chord',
      low,
      aria: `Bar ${number || 'pickup'}, beat ${position}${label ? `, ${label}${low ? ', less certain' : ''}` : ''}`,
    };
  });

  bars.forEach((bar, i) => {
    bar.end = bars[i + 1]?.start ?? gridEnd(grid, result.duration);
  });
  return bars;
}
